import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect
} from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { getAuthErrorMessage } from '../utils/authErrors';
import InstitutionalOnboardingService from '../../../services/InstitutionalOnboardingService';
import { completeParentSignup } from '../../../services/parentLinkingService';

const AuthContext = createContext();

// LocalStorage cache for auth state (shared across tabs)
const AUTH_CACHE_KEY = 'swan_auth_user';
const POPUP_LAUNCH_ERROR_CODES = new Set([
  'auth/popup-blocked',
  'auth/cancelled-popup-request'
]);

const createGoogleProvider = () => {
  const provider = new GoogleAuthProvider();
  provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
  provider.setCustomParameters({
    prompt: 'select_account'
  });
  return provider;
};

const newUnprivilegedUserProfile = (firebaseUser, profile = {}) => ({
  email: firebaseUser.email,
  ...(profile.displayName ? { displayName: profile.displayName } : {}),
  ...(profile.photoURL ? { photoURL: profile.photoURL } : {}),
  ...(profile.provider ? { provider: profile.provider } : {}),
  schoolId: null,
  assignedSchoolId: null,
  schoolIds: [],
  schoolRoles: {},
  linkedStudentIds: [],
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now(),
  admin: false,
  dev: false,
  staff: false,
  parent: false,
  canManageParentLinking: false,
  setupComplete: false,
});

/**
 * Save user to localStorage (shared across tabs)
 */
const saveUserToCache = (user) => {
  try {
    if (user) {
      // Only cache safe, serializable user properties
      const cacheData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified
      };
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cacheData));
      console.log('[useAuth] 💾 Saved user to localStorage cache (shared across tabs)');
    } else {
      localStorage.removeItem(AUTH_CACHE_KEY);
      console.log('[useAuth] 🗑️ Cleared user cache');
    }
  } catch (error) {
    console.warn('[useAuth] Failed to cache user:', error);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // A saved profile is not a Firebase session. Browser storage can be cleared
  // independently, and another tab can sign out. Only the auth observer may
  // expose a user to protected routes and school-data subscriptions.
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const [loading, setLoading] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Development bypass - auto-login as test user
    if (process.env.NODE_ENV === 'development' && window.location.search.includes('dev=true')) {
      const devUser = {
        uid: 'dev-user-123',
        email: 'test@swanschools.com',
        displayName: 'Test User',
        emailVerified: true
      };
      setUser(devUser);
      saveUserToCache(devUser);
      setInitializing(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      saveUserToCache(user); // Cache user state for next page load
      setInitializing(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email, password, options = {}) => {
    try {
      setError(null);
      setLoading(true);
      setSigningUp(true);

      // Create Firebase Auth user
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;

      console.log('[useAuth] 🆕 Created Firebase Auth user:', user.uid);
      const parentAccessCode = typeof options?.parentAccessCode === 'string'
        ? options.parentAccessCode
        : null;
      console.log('[useAuth] 📝 Creating user document with data:', {
        email: user.email,
        parentSignup: Boolean(parentAccessCode)
      });

      // The browser may create only an unprivileged profile. If this is a
      // Parent school flow, the callable revalidates the bearer code and adds
      // the bounded role/membership fields through the Admin SDK.
      const userDocData = newUnprivilegedUserProfile(user);
      await setDoc(doc(db, 'users', user.uid), userDocData);
      console.log('[useAuth] ✅ User document created successfully');
      if (parentAccessCode) {
        await completeParentSignup(parentAccessCode);
      }
      const isSchoolFlow = Boolean(parentAccessCode);
      console.log('[useAuth] 🔑 SETUP STATUS:', {
        setupComplete: isSchoolFlow,
        isSchoolFlow: isSchoolFlow,
        staff: false,
        parent: isSchoolFlow,
        willRedirectToSetup: !isSchoolFlow
      });
      console.log('[useAuth] Final user document data:', {
        uid: user.uid,
        email: userDocData.email,
        parent: isSchoolFlow
      });

      // Increased delay to allow Firestore replication (especially important for getUserInitData call)
      // Without this, getUserInitData might be called before the user doc is available
      // 2000ms to handle multi-region replication latency
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('[useAuth] ⏱️ Replication delay complete (2000ms)');

      // Check for institutional email auto-join AFTER creating user doc
      const institutionalSchool = await InstitutionalOnboardingService.checkInstitutionalEmail(
        user.email,
        user.uid
      );

      if (institutionalSchool) {
        console.log('[useAuth] User auto-joined institutional school:', institutionalSchool.name);
      }

      return user;
    } catch (error) {
      const friendlyError = getAuthErrorMessage(error);
      setError(friendlyError);
      throw error;
    } finally {
      setLoading(false);
      setSigningUp(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      const friendlyError = getAuthErrorMessage(error);
      setError(friendlyError);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      // Set signingUp BEFORE popup to prevent race condition:
      // onAuthStateChanged fires when signInWithPopup resolves, which would
      // cause PublicRoute to redirect before we've written the user doc.
      setSigningUp(true);
      const provider = createGoogleProvider();
      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupError) {
        if (POPUP_LAUNCH_ERROR_CODES.has(popupError?.code)) {
          await signInWithRedirect(auth, createGoogleProvider());
          return { redirecting: true };
        }
        throw popupError;
      }
      // User completed Google auth — now set loading while we write user doc
      setLoading(true);
      const user = result.user;

      // Get access token for People API
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;

      // Fetch actual photo from People API
      let actualPhotoURL = user.photoURL; // Fallback to default
      if (accessToken) {
        try {
          const peopleApiResponse = await fetch(
            'https://people.googleapis.com/v1/people/me?personFields=photos',
            {
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            }
          );

          if (peopleApiResponse.ok) {
            const peopleData = await peopleApiResponse.json();
            const primaryPhoto = peopleData.photos?.find(p => p.metadata?.primary);
            if (primaryPhoto?.url) {
              actualPhotoURL = primaryPhoto.url;
              console.log('[useAuth] ✅ Got actual photo from People API:', actualPhotoURL);
            }
          }
        } catch (peopleError) {
          console.warn('[useAuth] Failed to fetch photo from People API:', peopleError);
          // Continue with default photoURL
        }
      }

      // Check if user document already exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        // Existing user - only update safe fields
        console.log('[useAuth] 🔄 Existing Google user, updating fields');
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName,
          photoURL: actualPhotoURL,
          updatedAt: Timestamp.now()
        }, { merge: true });
        const pendingAccessCode = sessionStorage.getItem('pendingParentSetup');
        if (pendingAccessCode) {
          try {
            await completeParentSignup(pendingAccessCode);
          } finally {
            sessionStorage.removeItem('pendingParentSetup');
          }
        }
      } else {
        // New user - create with no school assignment initially
        console.log('[useAuth] 🆕 New Google user, creating document');

        // Check if this is a parent linking flow (code stored by LoginJoin).
        const pendingAccessCode = sessionStorage.getItem('pendingParentSetup');
        const isSchoolFlow = Boolean(pendingAccessCode);
        const newUserData = newUnprivilegedUserProfile(user, {
          displayName: user.displayName,
          photoURL: actualPhotoURL,
          provider: 'google',
        });

        await setDoc(userDocRef, newUserData);
        console.log('[useAuth] ✅ Google user document created');
        if (pendingAccessCode) {
          try {
            await completeParentSignup(pendingAccessCode);
          } finally {
            sessionStorage.removeItem('pendingParentSetup');
          }
        }
        console.log('[useAuth] 🔑 SETUP STATUS:', {
          setupComplete: isSchoolFlow,
          isSchoolFlow: isSchoolFlow,
          staff: false,
          parent: isSchoolFlow,
          willRedirectToSetup: !isSchoolFlow
        });
        console.log('[useAuth] User data:', {
          uid: user.uid,
          email: newUserData.email,
          displayName: newUserData.displayName,
          parent: isSchoolFlow
        });

        // Increased delay to allow Firestore replication
        // 2000ms to handle multi-region replication latency
        await new Promise(resolve => setTimeout(resolve, 2000));
        console.log('[useAuth] ⏱️ Replication delay complete (2000ms)');

        // Check for institutional email auto-join for new Google users
        const institutionalSchool = await InstitutionalOnboardingService.checkInstitutionalEmail(
          user.email,
          user.uid
        );

        if (institutionalSchool) {
          console.log('[useAuth] Google user auto-joined institutional school:', institutionalSchool.name);
        }
      }

      return user;
    } catch (error) {
      const friendlyError = getAuthErrorMessage(error);
      setError(friendlyError);
      throw error;
    } finally {
      setLoading(false);
      setSigningUp(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setError(null);
      setSigningUp(true);
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      setLoading(true);
      return result.user;
    } catch (error) {
      const friendlyError = getAuthErrorMessage(error);
      setError(friendlyError);
      throw error;
    } finally {
      setLoading(false);
      setSigningUp(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      saveUserToCache(null); // Clear cache on logout
    } catch (error) {
      const friendlyError = getAuthErrorMessage(error);
      setError(friendlyError);
      throw error;
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    loading,
    initializing,
    signingUp,
    error,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
